import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { FiUser, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import * as yup from "yup"
import FormInput from "../../components/forms/FormInput";
import { useEffect, useState } from "react";
import { useLoginMutation } from "../../features/auth/authApi";
import { useNavigate } from "react-router";
import BeatLoader from "react-spinners/BeatLoader"

const errorClassName = 'text-red-500 text-sm';

interface UserData {
    email: string;
    password: string;
}

const LoginPage = () => {
    const navigate = useNavigate();
    const [submitLogin, { isLoading }] = useLoginMutation();
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState<string>("");

    const schema = yup.object({
        email: yup.string().email("Must be a valid email").required("Please enter your email"),
        password: yup.string().min(8, "Password must be at least 8 characters").required("Please enter your password")
    })

    type LoginFormData = yup.InferType<typeof schema>
    const loginFormMethods = useForm<LoginFormData>({
        resolver: yupResolver(schema)
    })

    const onSubmit = async (formData: UserData) => {
        setLoginError("");
        try {
            const loginRes = await submitLogin(formData).unwrap();
            localStorage.setItem("accessToken", loginRes?.data?.accessToken);
            localStorage.setItem("refreshToken", loginRes?.data?.refreshToken);
            navigate("/");
        } catch (e: any) {
            const errorMessage = e?.data?.message || e?.message || "Invalid credentials. Please try again.";
            setLoginError(errorMessage);
        }
    };

    useEffect(() => {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
            navigate("/");
        }
    }, []);

    return <>
        <div className="w-full h-screen flex justify-center items-center bg-gray-100 relative">
            <div className="absolute top-6 left-6 z-10">
                <svg version="1.1" viewBox="0 0 2048 486" height="50" xmlns="http://www.w3.org/2000/svg">
                    <path transform="translate(31,68)" d="m0 0h367v364h-243l-1-159-11 15-11 21-5 15-4 17-1 8v10l2 9 5 8v6l-5 5-6-1-5-5-4-6-2-7v-29l5-21 7-19 10-19 13-17 1-1-111-1-1-30z" fill="#D8B387"></path>
                    <path transform="translate(817,109)" d="m0 0 12 2 12 6 10 9 8 10 8 16 5 17v21l-7 14-8 7-19 9-16 4-21 3-2 1-2 28v10l2 18 3 12 6 9 5 3h15l10-5 13-11 10-11 12-17 12-20 12-23 7-15 1-4 1-33 4-19 4-7 7-8 9-6 16-6h9l8 4 1 3-2 15-7 43-2 14-2 33 5-1 3-9 9-21 12-22 10-11 8-4 12-3h11l8 3 4 5-4 26-1 17 4-1 8-16 11-18 16-24 8-11 8-7 10-5 4-1h17l9 6 2 4v16l-8 44-8 54-1 13v13l3 3 5-1 2-4 7-9 2-4 8-14 13-29 12-36 5-12 8-14 6-8h2l2-4 11-9 13-6 6-1h14l15 3 16 8 8 7 9 10 8 16 4 12 2 10v29l-4 17-7 16-6 10-9 11-11 9-10 5-9 3-12 2-15-1-12-3-11-5-10-9-8-9-6-12-1-3-3-1-2 5-9 16-6 7-4 7-12 12-9 6-6 3-5 1h-9l-9-3-10-9-6-17-2-11v-34l3-22 2-12v-6l-5 2-11 20-8 15-10 21-13 26-8 8-11 5-9 1-4-2-3-5-2-13-1-9v-48h-4l-7 16-11 34-3 14-6 25-4 6-10 5-11 2-11-3-6-4-3-4-1-4v-21l-1-24-4 1-8 15-10 14-11 12-10 10-15 10-10 5-15 5-10 1h-13l-12-3-10-5-7-6-10-15-5-11-7-28h-3l-2 5-6 9-9 12-10 11-12 7-7 2h-10l-9-3-3-2v-2l-4-2-5-8-4-14-2-13v-25l3-26 2-13v-6l-4 1-15 27-27 54-7 8-8 5-8 3h-7l-5-6-2-8-2-32 1-31h-5l-5 13-4 9-4 15-3 8-5 19-4 20-4 9-9 6-11 3-11-2-6-3-5-5-2-4-1-55-2-42v-36l2-20 3-10 9-12 13-8 15-5 11 1 4 3 1 5-9 60-3 27-2 19h6l1-7 6-15 11-23 9-14 5-6 10-6 12-3h12l7 3 3 2 1 8-3 16-3 23 5-1 8-16 9-15 16-25 12-16 10-7 10-4h17l8 5 3 5v15l-6 36-4 22-4 33-2 11v25h5l4-2 2-4 6-8 12-23 10-23 7-18 3-12 3-14 9-25 11-20 9-11 10-9 8-4z" fill="#4E525F"></path>
                    <path transform="translate(1573,136)" d="m0 0 12 1 8 3 8 6 9 9 5-2 7-5 12-4h11l6 4 1 2v9l-2 15-1 18-1 35-1 20-1 54-1 29h19l13 1 6 3 4 5 1 5-7 8-5 2h-31l-1 68-3 6-5 4-5 2-6 1h-13l-10-3-3-5 1-29 1-24v-17l-25-1-10-3-4-4-2-4 1-5 4-5 8-3 29-1 1-41-9 6-14 9-6 2-9 1-13-2-9-5-11-11-11-21-2-1-3 10-11 18-9 10-7 7-9 5-4 1h-11l-10-3-9-6-9-10-5-12-3-9-5 5-7 12-7 9-13 13-12 7-8 2h-15l-10-5-7-8-6-15-4-17v-34l3-21 5-18 2-8-15 1-5 12-7 4-7-1-5-4-1-6 4-11v-6l-4-5-2-9 2-11 5-8 7-7 10-4 10 1 6 4 2 6-1 13-3 11 17-1h24l5 3v19l-7 36-3 24v19l3 13 4 2 5-1 5-6 10-19 12-28 12-36 1-7 1-24 3-12 2-4 8-5 4-1h10l8 2 6 4 7 9v15l-3 16-3 31-1 12v23l2 15 3 5 6 1 4-1 2-5 8-15 10-26 10-29 4-21 5-13 9-17 13-14 14-7z" fill="#4E525F"></path>
                    <path transform="translate(1911,110)" d="m0 0h16l12 5 10 7 9 10 8 14 5 13 2 7 1 10v10l-2 8-4 8-7 7-12 7-16 6-16 3-17 3-1 12v38l4 16 6 11 3 2 5 1h11l10-4 9-7 7-6 7-8 11-15 13-21 15-29 9-22 6-4h8l5 4 1 2v7l-6 19-8 22-13 27-12 19-12 14-9 9-14 10-16 8-12 4-9 1h-15l-12-3-11-6-10-11-8-14-5-14-2-11h-3l-2 5-5 9-8 11-8 8-8 5-8 2h-8l-12-3-9-6-9-9-6-12-3-16-1-9-4 1-2 8-5 12-9 19-12 16-9 8-5 2-8 1-10-2-9-5-9-8-7-13-2-8-1-10v-30l1-27 4-46 1-24 4-9 7-6 7-3 10-2 9 1 9 3 5 5 1 7-6 57-7 59v16l3 5 6-1 6-9 13-35 12-39 7-25 6-19 4-3 12-2 12 3 13 7 4 5 1 2-1 13-4 23-2 34v19l2 18 3 3 5 1 5-3 8-14 11-27 11-32 4-20 6-20 5-13 11-21 10-13 11-9z" fill="#4E525F"></path>
                    <path transform="translate(185,261)" d="m0 0h213v171h-226v-160l5 4 8 11 9 16 11 23 8 19 3 2 6-1 3-2v-7l-12-27-8-16-9-16-11-15z" fill="#D9B488"></path>
                    <path transform="translate(141,69)" d="m0 0h31l1 130 8-14 8-10 8-6 5-2 10-1 13 4 10 7 9 9 7 11 3 11-1 9-5 10-9 9-11 7h169l1 1v17h-213l13 19 9 16 8 16 11 25v7l-6 4-5-1-4-4-12-28-10-20-7-11-9-11-1 159h-17l-1-159-11 15-11 21-5 15-4 17-1 8v10l2 9 5 8v6l-5 5-6-1-5-5-4-6-2-7v-29l5-21 7-19 10-19 13-17 1-1-111-1v-18h53l16 1v-2l-6-2-11-7-10-9-7-8-5-11v-16l4-9 8-11 11-8 10-4h14l12 5 10 8 7 7 11 17 5 10 1-134h-14z" fill="#4E525F"></path>
                    <path transform="translate(32,261)" d="m0 0h112l-6 8-9 13-10 19-8 24-3 14v29l4 11 4 5 4 2 6-1 2-3v-6l-6-10-1-7v-10l3-17 5-18 11-23 10-14 5-5v160h-123l-1-24v-92z" fill="#D9B488"></path>
                    <path transform="translate(1456,62)" d="m0 0h20l13 4 10 6 6 9 2 10-4 11-7 8-10 7-16 6-5 1h-19l-16-4-8-4-7-9-2-7v-7l3-9 9-10 15-8z" fill="#4E525F"></path>
                    <path transform="translate(1577,164)" d="m0 0h9l7 4 6 11 3 10 1 7v36l-3 16-4 12-6 12-7 7-7 2-9-1-4-5-3-6-1-6-2-10-1-14v-16l2-21 4-15 5-12 6-8z" fill="#FCFBFA"></path>
                    <path transform="translate(1217,158)" d="m0 0h9l5 3 5 6 5 10 5 16 1 4 1 23v13l-2 15-3 10-6 11-6 4-10-1-7-4-8-14-4-12-2-21v-16l2-21 4-12 7-11z" fill="#FCFBFA"></path>
                    <path transform="translate(100,172)" d="m0 0 9 1 9 5 8 8 10 15 9 21 6 18v4l-9-2-17-5-19-8-14-9-9-8-6-9v-11l6-9 7-7z" fill="#D9B488"></path>
                    <path transform="translate(207,181)" d="m0 0 8 1 8 5 8 7 6 10 1 2v8l-4 6-9 7-16 8-19 6-14 3 2-12 9-27 6-11 9-10z" fill="#D9B488"></path>
                    <path transform="translate(824,140)" d="m0 0 6 1 4 8 1 7v22l-1 7-4 6-6 3-13 2h-7l1-8 5-20 6-17 6-10z" fill="#FBF9F6"></path>
                    <path transform="translate(1927,140)" d="m0 0 5 2 4 13 1 9v9l-2 11-4 6-7 4-11 2h-6l-1-4 5-21 6-18 4-8 4-4z" fill="#FBF9F6"></path>
                </svg>
            </div>
            <div className="w-4/5 max-w-4xl bg-white rounded-lg overflow-hidden flex shadow-lg">
                <div className="w-1/2 bg-white px-12 py-12 flex flex-col justify-center">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">Login</h1>
                    <p className="text-gray-600 mb-8">Sign In to your account</p>
                    
                    <FormProvider {...loginFormMethods}>
                        <div className="space-y-6">
                            <div className="relative">
                                <FiUser className="absolute left-3 top-3.5 z-10 text-gray-400" />
                                <FormInput
                                    name="email"
                                    type="text"
                                    register={loginFormMethods.register}
                                    errors={loginFormMethods.formState.errors}
                                    placeholder="Username"
                                    className="w-full border border-gray-300 py-3 pl-10 pr-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#dbb685]"
                                    errorClassName={errorClassName}
                                    containerClass="mb-0"
                                />
                            </div>

                            <div className="relative">
                                <FiLock className="absolute left-3 top-3.5 z-10 text-gray-400" />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    {...loginFormMethods.register("password")}
                                    placeholder="Password"
                                    className="w-full border border-gray-300 py-3 pl-10 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#dbb685]"
                                />
                                {loginFormMethods.formState.errors.password && (
                                    <p className={errorClassName + " mt-1"}>
                                        {loginFormMethods.formState.errors.password.message}
                                    </p>
                                )}
                            </div>

                            {loginError && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-red-600 text-sm">{loginError}</p>
                                </div>
                            )}

                            <div className="flex items-center justify-between mt-8">
                                <button 
                                    onClick={loginFormMethods.handleSubmit(onSubmit)} 
                                    disabled={isLoading}
                                    className="text-white px-8 py-3 rounded-md font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#dbb685' }}
                                    onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#c9a571')}
                                    onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#dbb685')}
                                >
                                    {isLoading ? (
                                        <BeatLoader size="6px" color="#fff" speedMultiplier={0.75} />
                                    ) : (
                                        "Login"
                                    )}
                                </button>
                            </div>
                        </div>
                    </FormProvider>
                </div>

                <div className="w-1/2 p-4">
                    <img 
                        src="/login_page_image.jpg" 
                        alt="Login" 
                        className="w-full h-full object-contain"
                    />
                </div>
            </div>
        </div>
    </>
}

export default LoginPage;